class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeInputs = this.querySelectorAll('input[type="range"]');
    this.numberInputs = this.querySelectorAll('input[type="number"], input[type="text"].field__input');
    this.track = this.querySelector('.price-slider-track');

    this.numberInputs.forEach((element) => {
      element.addEventListener('change', this.onNumberChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    this.rangeInputs.forEach((element) => {
      element.addEventListener('input', this.onSliderInput.bind(this));
      element.addEventListener('change', this.onSliderChange.bind(this));
    });

    this.setMinAndMaxValues();
    this.updateTrack();
  }

  onNumberChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.updateSlidersFromNumbers();
    this.updateTrack();
  }

  onSliderInput(event) {
    this.updateNumbersFromSliders(event.currentTarget);
    this.updateTrack();
  }

  onSliderChange(event) {
    this.updateNumbersFromSliders(event.currentTarget);
    this.updateTrack();
    
    // Trigger submit via facets.js logic
    const inputEvent = new Event('input', { bubbles: true });
    this.numberInputs[0].dispatchEvent(inputEvent);
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const minInput = this.numberInputs[0];
    const maxInput = this.numberInputs[1];
    if (!minInput || !maxInput) return;
    
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  updateNumbersFromSliders(sliderInput) {
    const minSlider = this.rangeInputs[0];
    const maxSlider = this.rangeInputs[1];
    const minNumber = this.numberInputs[0];
    const maxNumber = this.numberInputs[1];

    let minVal = parseInt(minSlider.value);
    let maxVal = parseInt(maxSlider.value);

    if (maxVal - minVal < 0) {
      if (sliderInput.classList.contains('min')) {
        minSlider.value = maxVal;
        minVal = maxVal;
      } else {
        maxSlider.value = minVal;
        maxVal = minVal;
      }
    }

    minNumber.value = minVal;
    maxNumber.value = maxVal;
  }

  updateSlidersFromNumbers() {
    if (this.rangeInputs.length === 0) return;
    this.rangeInputs[0].value = this.numberInputs[0].value || 0;
    this.rangeInputs[1].value = this.numberInputs[1].value || this.rangeInputs[1].max;
  }

  updateTrack() {
    if (!this.track) this.track = this.querySelector('.price-slider-track');
    if (!this.track || this.rangeInputs.length === 0) return;
    
    const minSlider = this.rangeInputs[0];
    const maxSlider = this.rangeInputs[1];
    
    const minVal = parseFloat(minSlider.value) || 0;
    const maxVal = parseFloat(maxSlider.value) || 0;
    const maxLimit = parseFloat(minSlider.max) || 100;
    
    const minPercent = (minVal / maxLimit) * 100;
    const maxPercent = (maxVal / maxLimit) * 100;
    
    this.track.style.left = `${minPercent.toFixed(2)}%`;
    this.track.style.width = `${(maxPercent - minPercent).toFixed(2)}%`;
    this.track.style.display = 'block';
    this.track.style.backgroundColor = '#2C3B2E';
    this.track.style.height = '100%';
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Custom Premium Toolbar Javascript
document.addEventListener('DOMContentLoaded', () => {
  // 1. Hide Filters Toggle
  const hideBtn = document.getElementById('CustomHideFiltersBtn');
  const facetsWrapper = document.getElementById('main-collection-filters');
  const facetsVertical = document.querySelector('.facets-vertical');
  const hideText = hideBtn?.querySelector('.hide-text');
  const showText = hideBtn?.querySelector('.show-text');

  if (hideBtn && facetsWrapper && facetsVertical) {
    hideBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const isHidden = facetsWrapper.classList.toggle('is-hidden');
      facetsVertical.classList.toggle('filters-hidden-layout');
      hideBtn.classList.toggle('filters-hidden');
      
      if (isHidden) {
        hideText.classList.add('hidden');
        showText.classList.remove('hidden');
      } else {
        hideText.classList.remove('hidden');
        showText.classList.add('hidden');
      }
    });
  }

  // 2. Grid View Toggles
  const gridButtons = document.querySelectorAll('.grid-toggle-btn');
  // Need to use body event delegation because Shopify AJAX reloading replaces the product grid
  document.body.addEventListener('click', (e) => {
    const btn = e.target.closest('.grid-toggle-btn');
    if (!btn) return;
    
    e.preventDefault();
    const col = btn.getAttribute('data-col');
    setGridColumns(col);
    localStorage.setItem('customGridPref', col);
  });

  function setGridColumns(col) {
    const productGrid = document.querySelector('#product-grid');
    if (!productGrid) return;
    
    const allGridButtons = document.querySelectorAll('.grid-toggle-btn');
    allGridButtons.forEach(b => b.classList.remove('active'));
    
    const activeBtn = Array.from(allGridButtons).find(b => b.getAttribute('data-col') === col);
    if (activeBtn) activeBtn.classList.add('active');

    productGrid.className = productGrid.className.replace(/grid--\d-col-desktop/g, '');
    productGrid.classList.add(`grid--${col}-col-desktop`);
  }

  // Re-apply grid layout when navigating or AJAX filtering finishes
  const savedCol = localStorage.getItem('customGridPref');
  if (savedCol) {
    setGridColumns(savedCol);
  }
  
  // Shopify Facets AJAX listener to re-apply the grid settings when products reload
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length && mutation.addedNodes[0].id === 'product-grid') {
        const currentSavedCol = localStorage.getItem('customGridPref');
        if (currentSavedCol) {
          setGridColumns(currentSavedCol);
        }
      }
    });
  });
  
  const gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer) {
    observer.observe(gridContainer, { childList: true, subtree: true });
  }
});

// --- Peonika Toolbar JS ---
document.addEventListener('click', (e) => {
  // 1. Hide Filters Toggle
  const hideBtn = e.target.closest('#CustomHideFiltersBtn');
  if (hideBtn) {
    const facetsContainer = document.querySelector('.facets-vertical');
    if (facetsContainer) {
      facetsContainer.classList.toggle('filters-hidden');
      const isHidden = facetsContainer.classList.contains('filters-hidden');
      hideBtn.classList.toggle('filters-hidden', isHidden);
      hideBtn.querySelector('.hide-text').classList.toggle('hidden', isHidden);
      hideBtn.querySelector('.show-text').classList.toggle('hidden', !isHidden);
    }
  }

  // 2. Grid Toggle
  const gridBtn = e.target.closest('.grid-toggle-btn');
  if (gridBtn) {
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      // remove active class from all
      document.querySelectorAll('.grid-toggle-btn').forEach(b => b.classList.remove('active'));
      // add to current
      gridBtn.classList.add('active');
      
      const col = gridBtn.getAttribute('data-col');
      
      // Remove existing grid--X-col-desktop classes
      const classesToRemove = Array.from(productGrid.classList).filter(c => c.match(/grid--\d-col-desktop/));
      classesToRemove.forEach(c => productGrid.classList.remove(c));
      
      // Add new class
      productGrid.classList.add(`grid--${col}-col-desktop`);
    }
  }

  // 3. Custom Dropdown logic
  const optionBtn = e.target.closest('.custom-dropdown-option');
  
  if (optionBtn) {
    const dropdownContainer = optionBtn.closest('.custom-select-dropdown');
    if (dropdownContainer) {
      const sortSelect = dropdownContainer.querySelector('select');
      const dropdownDetails = dropdownContainer.querySelector('.custom-dropdown-details');
      
      if (sortSelect) {
        const value = optionBtn.getAttribute('data-value');
        
        // Update select
        sortSelect.value = value;
        
        // Dispatch change event to trigger Dawn's FacetFiltersForm
        sortSelect.dispatchEvent(new Event('input', { bubbles: true }));
        sortSelect.dispatchEvent(new Event('change', { bubbles: true }));
        
        if (dropdownDetails) {
          dropdownDetails.removeAttribute('open');
        }
      }
    }
  }

  // Click outside to close dropdowns
  document.querySelectorAll('.custom-dropdown-details[open]').forEach(details => {
    if (!e.target.closest('.custom-select-dropdown')) {
      details.removeAttribute('open');
    }
  });
});
